import axiosInstance from '../utils/axiosInstance';

const getDataResult = (response) => response.data?.data ?? response.data ?? {};

export const fetchDeviceStatus = async () => {
  const response = await axiosInstance.get('/metrics/status');
  const payload = getDataResult(response);

  return payload.result ?? payload ?? [];
};

export const fetchOnlineCount = async () => {
  const response = await axiosInstance.get('/metrics/online-count');
  const payload = getDataResult(response);
  return parseInt(payload.result?.[0]?.value?.[1] ?? payload.value ?? 0, 10) || 0;
};

export const fetchOfflineCount = async () => {
  const response = await axiosInstance.get('/metrics/offline-count');
  const payload = getDataResult(response);
  return parseInt(payload.result?.[0]?.value?.[1] ?? payload.value ?? 0, 10) || 0;
};

export const fetchUnstableRouters = async () => {
  const response = await axiosInstance.get('/metrics/unstable');
  return getDataResult(response) ?? [];
};

export const fetchAverageUtilizationPercent = async () => {
  const response = await axiosInstance.get('/metrics/utilization/percentage');
  const payload = getDataResult(response);
  return parseFloat(payload.value ?? payload?.data?.value ?? 0) || 0;
};

export const fetchTopConsumers = async () => {
  const response = await axiosInstance.get('/metrics/top-consumers');
  return getDataResult(response) ?? [];
};

export const fetchTotalDevices = async () => {
  const response = await axiosInstance.get('/metrics/total-devices');
  const payload = getDataResult(response);
  return parseInt(payload.value ?? payload?.data?.value ?? 0, 10) || 0;
};

export const fetchUtilizationSeries = async (params) => {
  const response = await axiosInstance.get('/metrics/utilization', { params });
  const payload = getDataResult(response);
  return {
    in: payload.in ?? [],
    out: payload.out ?? [],
  };
};

export const fetchDeviceUtilizationSeries = async (deviceIp, params) => {
  const response = await axiosInstance.get(`/metrics/utilization/${deviceIp}`, { params });
  const payload = getDataResult(response);
  return {
    in: payload.in ?? [],
    out: payload.out ?? [],
  };
};

export const fetchSchoolMarkers = async () => {
  const response = await axiosInstance.get('/metrics/map-devices');
  return getDataResult(response) ?? [];
};

export const fetchDevicesDetails = async () => {
  const response = await axiosInstance.get('/metrics/devices-details');
  return getDataResult(response) ?? [];
};

export const fetchSchoolOverview = async (ip) => {
  if (!ip) throw new Error('Не указан IP устройства');
  const response = await axiosInstance.get(`/metrics/schools/${encodeURIComponent(ip)}/overview`);
  return getDataResult(response) ?? {};
};

export const fetchSpeedtestResults = async (params = {}) => {
  const response = await axiosInstance.get('/metrics/speedtest-results', { params });
  return response.data ?? {};
};

export const fetchDeviceLogs = async (params = {}) => {
  const response = await axiosInstance.get('/metrics/logs', { params });
  return response.data ?? {};
};

export const fetchAlerts = async () => {
  const response = await axiosInstance.get('/metrics/alerts');
  const payload = getDataResult(response);
  return Array.isArray(payload) ? payload : payload.result ?? payload.data ?? [];
};

export const acknowledgeAlert = async (fingerprint, { comment, durationMinutes } = {}) => {
  if (!fingerprint) throw new Error('Не указан fingerprint алерта');

  const response = await axiosInstance.post(`/metrics/alerts/${encodeURIComponent(fingerprint)}/ack`, {
    comment,
    durationMinutes,
  });

  return getDataResult(response);
};

export const fetchAverageSpeedtest = async () => {
  const response = await axiosInstance.get('/metrics/speedtest/average');
  const payload = getDataResult(response);
  return {
    download: payload.data?.avg_download ?? payload.avg_download ?? 0,
    upload: payload.data?.avg_upload ?? payload.avg_upload ?? 0,
  };
};

export const runSpeedtest = async (ip) => {
  const response = await axiosInstance.post('/metrics/speed-test', { ip });
  return getDataResult(response);
};

export const fetchLastSpeedtest = async (ip) => {
  if (!ip) throw new Error('Не указан IP устройства');
  // Используем query параметр для большей надежности
  const response = await axiosInstance.get(`/metrics/speedtest/last`, {
    params: { ip: ip }
  });
  return getDataResult(response);
};


export const fetchTrends = async (metricName, periodType = 'daily', limit = 10) => {
  const params = { metricName, periodType, limit };
  const response = await axiosInstance.get('/metrics/trends', { params });
  return getDataResult(response) ?? [];
};

export const fetchDailyStats = async (date) => {
  const params = date ? { date } : {};
  const response = await axiosInstance.get('/metrics/daily-stats', { params });
  return getDataResult(response) ?? [];
};

export const fetchTotalTraffic = async () => {
  const response = await axiosInstance.get('/metrics/total-traffic');
  return getDataResult(response) ?? { total_traffic_gb: 0, date: null };
};

export const fetchCurrentTraffic = async () => {
  const response = await axiosInstance.get('/metrics/current-traffic');
  return getDataResult(response) ?? { current_traffic_gb: 0 };
};

export const fetchStatsByRegions = async () => {
  const response = await axiosInstance.get('/metrics/stats-by-regions');
  return getDataResult(response) ?? [];
};

export const updateDeviceInfo = async (ip, { newIp, eshdiVlanId, speedtestTargetServer }) => {
  if (!ip) throw new Error('Не указан IP устройства');
  const response = await axiosInstance.put(`/metrics/schools/${encodeURIComponent(ip)}/update`, {
    newIp,
    eshdiVlanId,
    speedtestTargetServer,
  });
  return getDataResult(response);
};

export const updateObjectVisible = async (ip, visible) => {
  if (!ip) throw new Error('Не указан IP устройства');
  const response = await axiosInstance.put(`/metrics/schools/${encodeURIComponent(ip)}/visible`, {
    visible,
  });
  return getDataResult(response);
};

export const fetchDeviceInterfaces = async (ip) => {
  if (!ip) throw new Error('Не указан IP устройства');
  const response = await axiosInstance.get(`/metrics/schools/${encodeURIComponent(ip)}/interfaces`);
  return getDataResult(response);
};

export const updateObjectInfo = async (ip, { name, region, city, additional_url, photo_url }) => {
  if (!ip) throw new Error('Не указан IP устройства');
  const response = await axiosInstance.put(`/metrics/schools/${encodeURIComponent(ip)}/object-info`, {
    name,
    region,
    city,
    additional_url,
    photo_url,
  });
  return getDataResult(response);
};

export const fetchRegionsAndCities = async () => {
  const response = await axiosInstance.get('/metrics/regions-and-cities');
  return getDataResult(response) ?? { regions: [], cities: [] };
};

export const fetchLabels = async () => {
  const response = await axiosInstance.get('/metrics/labels');
  const payload = getDataResult(response);
  return Array.isArray(payload) ? payload : [];
};

export const createLabel = async (label, color) => {
  const response = await axiosInstance.post('/metrics/labels', { label, color });
  return getDataResult(response);
};

export const updateDeviceLabel = async (ip, labelId) => {
  if (!ip) throw new Error('Не указан IP устройства');
  const response = await axiosInstance.put(`/metrics/schools/${encodeURIComponent(ip)}/label`, {
    labelId: labelId === undefined ? null : labelId,
  });
  return getDataResult(response);
};

export const createSchool = async (schoolData) => {
  const response = await axiosInstance.post('/metrics/schools', schoolData);
  return getDataResult(response);
};

export const deleteSchool = async (ip) => {
  if (!ip) throw new Error('Не указан IP устройства');
  const response = await axiosInstance.delete(`/metrics/schools/${encodeURIComponent(ip)}`);
  return getDataResult(response);
};

